/**
 * Revenue Trend Chart Component
 * Shows revenue over time with area chart (matching Portfolio Growth style)
 */

import React, { useEffect, useState, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { Box, Text, useColorModeValue, useColorMode, useToken, Flex, Select } from "@chakra-ui/react";
import Card from "../../../../components/Card/Card";
import CardBody from "../../../../components/Card/CardBody";
import CardHeader from "../../../../components/Card/CardHeader";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";

interface RevenueTrendChartProps {
  timeframe?: string;
  onTimeframeChange?: (timeframe: string) => void;
}

const RevenueTrendChart: React.FC<RevenueTrendChartProps> = ({ 
  timeframe = "all-time",
  onTimeframeChange 
}) => {
  const { formatMoney, revision } = useDisplayCurrency();
  const textColor = useColorModeValue("#575757", "white");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const { colorMode } = useColorMode();
  const [gray800, gray700] = useToken("colors", ["gray.800", "gray.700"]);

  // Mock revenue data - filter based on timeframe
  // Revenue = subscriptions * $50/month
  // Starting with ~200 subscriptions in Jan, growing to 850 in Dec
  // Accounting for growth and churn (2.3% monthly churn)
  const allRevenueData = [
    { month: "Jan", revenue: 10000 },   // ~200 subscriptions
    { month: "Feb", revenue: 12500 },   // ~250 subscriptions
    { month: "Mar", revenue: 15000 },   // ~300 subscriptions
    { month: "Apr", revenue: 20000 },   // ~400 subscriptions
    { month: "May", revenue: 25000 },   // ~500 subscriptions
    { month: "Jun", revenue: 30000 },   // ~600 subscriptions
    { month: "Jul", revenue: 35000 },  // ~700 subscriptions
    { month: "Aug", revenue: 37500 },   // ~750 subscriptions
    { month: "Sep", revenue: 40000 },   // ~800 subscriptions
    { month: "Oct", revenue: 41250 },  // ~825 subscriptions
    { month: "Nov", revenue: 42500 },  // ~850 subscriptions
    { month: "Dec", revenue: 42500 },  // 850 subscriptions (current MRR)
  ];

  // Filter data based on timeframe
  const revenueData = useMemo(() => {
    if (timeframe === "all-time") {
      return allRevenueData;
    }
    // For days, show approximate months (7 days ≈ 1 month, 14 days ≈ 1 month, 30 days ≈ 1 month)
    const monthsToShow = timeframe === "7-days" ? 1 : timeframe === "14-days" ? 1 : timeframe === "30-days" ? 1 : 12;
    return allRevenueData.slice(-monthsToShow);
  }, [timeframe]);

  // Add CSS to ensure grid lines are visible (matching PortfolioGrowthChart)
  useEffect(() => {
    const styleId = "revenue-trend-grid-styles";
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      .apexcharts-svg .apexcharts-gridline:not([class*="heatmap"]),
      .apexcharts-svg .apexcharts-gridlines-horizontal:not([class*="heatmap"]) line,
      .apexcharts-svg .apexcharts-gridlines-vertical:not([class*="heatmap"]) line {
        stroke-dasharray: 5 5 !important;
        stroke: #e7e7e7 !important;
        opacity: 1 !important;
        display: block !important;
        visibility: visible !important;
      }
      .apexcharts-svg .apexcharts-gridlines-horizontal:not([class*="heatmap"]),
      .apexcharts-svg .apexcharts-gridlines-vertical:not([class*="heatmap"]) {
        display: block !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [colorMode, gray800, gray700]);

  // Tooltip styles with solid background (matching PortfolioGrowthChart)
  useEffect(() => {
    const styleId = "revenue-trend-tooltip-styles";
    const existingStyle = document.getElementById(styleId);
    if (existingStyle) {
      existingStyle.remove();
    }

    const style = document.createElement("style");
    style.id = styleId;
    const tooltipBg = colorMode === "dark" ? gray700 : gray800;
    const tooltipBorder = colorMode === "dark" ? "rgba(255, 255, 255, 0.2)" : "rgba(87, 87, 87, 0.2)";
    
    style.textContent = `
      .apexcharts-tooltip.apexcharts-theme-dark {
        background: ${tooltipBg} !important;
        background-color: ${tooltipBg} !important;
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
        border: 1px solid ${tooltipBorder} !important;
        border-radius: 12px !important;
        box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37) !important;
        padding: 12px 16px !important;
        color: #ffffff !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark,
      .apexcharts-tooltip.apexcharts-theme-dark * {
        background: ${tooltipBg} !important;
        background-color: ${tooltipBg} !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark:empty {
        display: none !important;
      }
      .apexcharts-tooltip.apexcharts-theme-dark[style*="opacity: 0"],
      .apexcharts-tooltip.apexcharts-theme-dark[style*="display: none"] {
        display: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleToRemove = document.getElementById(styleId);
      if (styleToRemove) {
        styleToRemove.remove();
      }
    };
  }, [colorMode, gray800, gray700]);

  const chartOptions: ApexOptions = useMemo(() => ({
    chart: {
      type: "area",
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
      },
    },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: ["#17FEAE"],
      show: true,
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "vertical",
        shadeIntensity: 0.5,
        gradientToColors: ["#17FEAE"],
        inverseColors: false,
        opacityFrom: 0.2,
        opacityTo: 0.05,
        stops: [0, 100],
      },
      colors: ["#17FEAE"],
      opacity: 0.2,
    },
    colors: ["#17FEAE"],
    dataLabels: {
      enabled: false,
    },
    grid: {
      strokeDashArray: 5,
      borderColor: "#e7e7e7",
    },
    xaxis: {
      categories: revenueData.map((d) => d.month),
      labels: {
        style: {
          colors: "#c8cfca",
          fontSize: "12px",
        },
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        maxHeight: 60,
      },
      crosshairs: {
        show: true,
        stroke: {
          color: "#e0e0e0",
          width: 1,
          dashArray: 5,
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      labels: {
        show: true,
        style: {
          colors: "#c8cfca",
          fontSize: "12px",
        },
        formatter: function (val: number) {
          return formatMoney(val);
        },
      },
    },
    tooltip: {
      theme: "dark",
      enabled: true,
      followCursor: false,
      hideEmptySeries: true,
      y: {
        formatter: function (val: number) {
          return formatMoney(val);
        },
      },
    },
    legend: {
      show: false,
    },
  }), [revenueData, formatMoney, revision]);

  const chartSeries = [
    {
      name: "Revenue",
      data: revenueData.map((d) => d.revenue),
    },
  ];

  return (
    <Card bg={cardBg} borderRadius="md" border="1px solid" borderColor={borderColor}>
      <CardHeader pb="0" position="relative">
        <Flex direction="column" alignSelf="flex-start" w="100%">
          <Text fontSize="sm" color="gray.400" fontWeight="medium" mb="6px" fontFamily="'IBM Plex Sans', sans-serif" textTransform="uppercase">
            Revenue Trend
          </Text>
        </Flex>
        {onTimeframeChange && (
          <Box position="absolute" top="0" right="0">
            <Select
              value={timeframe}
              onChange={(e) => onTimeframeChange(e.target.value)}
              size="xs"
              bg="transparent"
              color="gray.400"
              border="none"
              fontSize={{ base: '10px', sm: '11px', md: '12px' }}
              fontWeight="medium"
              fontFamily="'IBM Plex Sans', sans-serif"
              textTransform="uppercase"
              minW="auto"
              w="auto"
              _hover={{ bg: "transparent" }}
              _focus={{ bg: "transparent", boxShadow: "none" }}
              sx={{
                option: {
                  bg: "gray.800",
                  color: "white",
                },
                width: "fit-content !important",
                '& fieldset': {
                  paddingRight: "0px !important",
                  paddingLeft: "0px !important",
                  '& > select': {
                    paddingRight: "16px !important",
                    paddingLeft: "0px !important",
                    width: "fit-content !important",
                  },
                },
                '& > select': {
                  overflow: "visible",
                  textOverflow: "clip",
                  paddingRight: "16px !important",
                  width: "fit-content !important",
                },
                '& [data-icon]': {
                  right: "0px !important",
                  left: "auto !important",
                },
                '& .chakra-select__icon-wrapper': {
                  right: "0px !important",
                  left: "auto !important",
                  width: "16px !important",
                },
                '& .chakra-select__wrapper': {
                  paddingRight: "0px !important",
                  width: "fit-content !important",
                },
              }}
            >
              <option value="today">Today</option>
              <option value="7-days">7 days</option>
              <option value="14-days">14 days</option>
              <option value="30-days">30 days</option>
              <option value="all-time">All time</option>
            </Select>
          </Box>
        )}
      </CardHeader>
      <CardBody>
        <Box w="100%" h="350px">
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="area"
            height="100%"
            width="100%"
          />
        </Box>
      </CardBody>
    </Card>
  );
};

export default RevenueTrendChart;

